/**
 * 🖤 SHADOW MODE - Le Côté Obscur de Heartbeats
 * 
 * "Dans l'ombre, on ne ment plus..."
 * 
 * Un monde parallèle où les utilisateurs assument leurs désirs,
 * guidés par les 7 péchés capitaux.
 * 
 * Dark mais élégant. Provocateur mais jamais vulgaire.
 * Comme une nuit parisienne dans un club privé.
 * 
 * Guides de l'Ombre :
 * - NYX (🖤) - Guide féminin, underground chic
 * - KURSON (🔮) - Guide masculin, bad boy oracle
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Moon, Sun, Flame, Heart, X, MessageCircle, Sparkles, 
  Users, ChevronRight, Eye, EyeOff, Crown, Zap, Wine, Gem,
  Lock, Unlock, Settings
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { toast } from 'sonner';
import { api } from '../../../services/api';

import { ShadowGuide, getShadowGuideForUser } from './ShadowGuides';
import { ShadowQuiz, SEVEN_SINS, SinBadge, ShadowCompatibilityBadge, calculateShadowCompatibility } from './ShadowSins';
import { Nyx2Quiz, ATTACHMENT_STYLES, FIVE_WOUNDS } from './Nyx2Deep';
import { GUIDE_IMAGES } from '../soul/GuideAvatar';

// 🏠 SALONS DE L'OMBRE - Un pour chaque péché
export const SHADOW_ROOMS = [
  {
    id: 'luxuria_lounge',
    name: '🔥 Le Boudoir de Luxure',
    sin: 'luxuria',
    description: 'Où les désirs s\'expriment sans filtre...',
    emoji: '🔥',
    color: 'from-red-900 to-red-950',
    maxUsers: 30,
    isHot: true,
    welcomeMessage: 'Bienvenue au Boudoir... Ici, le désir est roi. 🔥',
    rules: 'Assumé(e) mais respectueux(se). La passion, pas la vulgarité.',
  },
  {
    id: 'superbia_palace',
    name: '👑 Le Palais de l\'Orgueil',
    sin: 'superbia',
    description: 'Où les égos s\'affrontent et s\'admirent...',
    emoji: '👑',
    color: 'from-yellow-900 to-amber-950',
    maxUsers: 25,
    welcomeMessage: 'Bienvenue au Palais... Seuls les meilleurs entrent ici. 👑',
    rules: 'Montrez votre valeur. L\'arrogance a du charme.',
  },
  {
    id: 'invidia_garden',
    name: '💚 Le Jardin d\'Envie',
    sin: 'invidia',
    description: 'Où l\'on convoite ce que les autres ont...',
    emoji: '💚',
    color: 'from-green-900 to-emerald-950',
    maxUsers: 25,
    welcomeMessage: 'Bienvenue au Jardin... Qu\'est-ce que tu veux vraiment ? 💚',
    rules: 'L\'envie est un moteur. Assumez vos désirs de conquête.',
  },
  {
    id: 'gula_feast',
    name: '🍷 Le Festin de Gourmandise',
    sin: 'gula',
    description: 'Où l\'on goûte à tout sans modération...',
    emoji: '🍷',
    color: 'from-purple-900 to-fuchsia-950',
    maxUsers: 35,
    isHot: true,
    welcomeMessage: 'Bienvenue au Festin... La vie est un buffet, goûtons à tout ! 🍷',
    rules: 'Épicuriens bienvenus. Pas de jugement sur les appétits.',
  },
  {
    id: 'avaritia_vault',
    name: '💎 Le Coffre d\'Avarice',
    sin: 'avaritia',
    description: 'Où l\'on collectionne les trésors rares...',
    emoji: '💎',
    color: 'from-cyan-900 to-blue-950',
    maxUsers: 20,
    isExclusive: true,
    welcomeMessage: 'Bienvenue au Coffre... Seules les pièces rares sont admises. 💎',
    rules: 'Qualité > Quantité. Vous êtes ici pour le meilleur.',
  },
  {
    id: 'ira_arena',
    name: '⚡ L\'Arène de Colère',
    sin: 'ira',
    description: 'Où les passions s\'affrontent...',
    emoji: '⚡',
    color: 'from-orange-900 to-red-950',
    maxUsers: 25,
    welcomeMessage: 'Bienvenue à l\'Arène... Prêt(e) pour les étincelles ? ⚡',
    rules: 'Débats passionnés, pas d\'insultes. La colère est une forme d\'amour.',
  },
  {
    id: 'acedia_sanctuary',
    name: '🌙 Le Sanctuaire de Paresse',
    sin: 'acedia',
    description: 'Où l\'on attend que les autres viennent à nous...',
    emoji: '🌙',
    color: 'from-indigo-900 to-purple-950',
    maxUsers: 30,
    welcomeMessage: 'Bienvenue au Sanctuaire... Détends-toi, ils viendront à toi. 🌙',
    rules: 'Ici on attire, on ne chasse pas. Le mystère est votre arme.',
  },
];

// 🎨 SHADOW ROOM CARD
const ShadowRoomCard = ({ room, onlineCount = 0, onClick, isJoined = false, userSin = null }) => {
  const sinInfo = SEVEN_SINS[room.sin];
  const isCompatible = userSin && sinInfo?.compatibleWith?.includes(userSin);
  
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-2xl cursor-pointer bg-black border border-gray-800 shadow-lg`}
    >
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${room.color} opacity-40`} />
      
      {/* Smoke effect */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-full h-8 bg-white/5"
            style={{ top: `${30 + i * 25}%` }}
            animate={{
              x: ['-100%', '100%'],
              opacity: [0, 0.3, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              delay: i * 2,
            }}
          />
        ))}
      </div>
      
      {/* Content */}
      <div className="relative p-4">
        {/* Badges */}
        <div className="absolute top-2 right-2 flex gap-1">
          {room.isHot && (
            <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold animate-pulse">
              🔥 HOT
            </span>
          )}
          {room.isExclusive && (
            <span className="px-2 py-0.5 rounded-full bg-purple-500 text-white text-xs font-bold">
              💎 VIP
            </span>
          )}
          {isCompatible && (
            <span className="px-2 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">
              ✓ Compatible
            </span>
          )}
        </div>
        
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <motion.div 
            animate={{ 
              boxShadow: [
                `0 0 10px ${sinInfo?.emoji === '🔥' ? 'rgba(239,68,68,0.5)' : 'rgba(168,85,247,0.5)'}`,
                `0 0 20px ${sinInfo?.emoji === '🔥' ? 'rgba(239,68,68,0.8)' : 'rgba(168,85,247,0.8)'}`,
                `0 0 10px ${sinInfo?.emoji === '🔥' ? 'rgba(239,68,68,0.5)' : 'rgba(168,85,247,0.5)'}`,
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className={`w-12 h-12 rounded-xl bg-gradient-to-br ${room.color} flex items-center justify-center text-2xl`}
          >
            {room.emoji}
          </motion.div>
          <div className="flex-1">
            <h3 className="font-bold text-white">{room.name}</h3>
            <p className="text-xs text-gray-400">{room.description}</p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-xs text-red-400">{onlineCount} âmes</span>
            </div>
          </div>
          
          {isJoined && (
            <span className="px-2 py-0.5 rounded-full bg-purple-500/30 text-purple-300 text-xs">
              ✓ Initié
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// 🌙 TOGGLE LIGHT/SHADOW MODE
export const ModeToggle = ({ isShad, onToggle, className = '' }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onToggle}
      className={`relative p-1 rounded-full ${
        isShad 
          ? 'bg-gradient-to-r from-purple-900 to-black' 
          : 'bg-gradient-to-r from-pink-500 to-purple-500'
      } ${className}`}
    >
      <motion.div
        className={`w-12 h-6 rounded-full flex items-center ${
          isShad ? 'justify-end' : 'justify-start'
        }`}
        layout
      >
        <motion.div
          layout
          className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
            isShad ? 'bg-purple-500' : 'bg-white'
          }`}
        >
          {isShad ? '🌙' : '☀️'}
        </motion.div>
      </motion.div>
    </motion.button>
  );
};

// 🖤 SHADOW MODE MAIN COMPONENT
export const ShadowMode = ({
  isOpen,
  onClose,
  customerId,
  customer,
  profile,
  isDarkMode = true,
}) => {
  const [shadowProfile, setShadowProfile] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [guideType, setGuideType] = useState('welcome');
  const [activeRoom, setActiveRoom] = useState(null);
  const [joinedRooms, setJoinedRooms] = useState([]);
  const [onlineCounts, setOnlineCounts] = useState({});
  const [loading, setLoading] = useState(true);
  
  // 🌙 NYX 2.0 - États pour le quiz profond
  const [showNyx2Quiz, setShowNyx2Quiz] = useState(false);
  const [nyx2Profile, setNyx2Profile] = useState(null);
  
  // Déterminer le guide
  const guideCharacter = getShadowGuideForUser(
    customer?.gender || 'male',
    profile?.lookingFor || 'all'
  );
  
  // Charger le profil shadow
  useEffect(() => {
    if (isOpen && customerId) {
      loadShadowProfile();
      simulateOnlineCounts();
    }
  }, [isOpen, customerId]);
  
  const loadShadowProfile = async () => {
    setLoading(true);
    try {
      const res = await api.getShadowProfile(customerId);
      setShadowProfile(res.data);
      
      // Charger aussi le profil Nyx 2.0
      try {
        const nyx2Res = await api.getNyx2Profile(customerId);
        if (nyx2Res.completed) {
          setNyx2Profile(nyx2Res.profile);
        }
      } catch (e) {
        // Pas de profil Nyx 2.0 encore
      }
      
      // Premier accès ? Montrer le guide
      if (!res.data?.quizCompleted) {
        setShowGuide(true);
        setGuideType('welcome');
      }
    } catch (error) {
      // Pas de profil shadow = nouveau
      setShadowProfile(null);
      setShowGuide(true);
      setGuideType('welcome');
    }
    setLoading(false);
  };
  
  const simulateOnlineCounts = () => {
    const counts = {};
    SHADOW_ROOMS.forEach(room => {
      counts[room.id] = Math.floor(Math.random() * 15) + 3;
    });
    setOnlineCounts(counts);
  };
  
  const handleQuizComplete = async (quizResult) => {
    try {
      await api.saveShadowProfile(customerId, quizResult);
      setShadowProfile(quizResult);
      setShowQuiz(false);
      
      // Montrer le guide avec le reveal du péché
      setGuideType('sinReveal');
      setShowGuide(true);
      
      toast.success(`🖤 Ton ombre s'est révélée : ${SEVEN_SINS[quizResult.dominantSin].name}!`);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };
  
  // 🌙 NYX 2.0 - Sauvegarder le profil profond
  const handleNyx2Complete = async (nyx2Result) => {
    try {
      await api.saveNyx2Profile(customerId, nyx2Result);
      setNyx2Profile(nyx2Result);
      setShowNyx2Quiz(false);
      
      const attachmentName = ATTACHMENT_STYLES[nyx2Result.attachmentStyle]?.name;
      const woundName = FIVE_WOUNDS[nyx2Result.dominantWound]?.name;
      
      toast.success(`🌙 Tes profondeurs révélées ! Attachement: ${attachmentName}, Blessure: ${woundName}`);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde Nyx 2.0');
    }
  };
  
  const handleJoinRoom = (room) => {
    if (!joinedRooms.includes(room.id)) {
      setJoinedRooms([...joinedRooms, room.id]);
      toast.success(`🖤 Tu as rejoint "${room.name}"`);
    }
    setActiveRoom(room);
  };
  
  // Si le quiz n'est pas fait, afficher le quiz
  const needsQuiz = !shadowProfile?.quizCompleted;
  
  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-x-hidden overflow-y-auto bg-black text-white border-gray-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Moon className="w-5 h-5 text-purple-400" />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-black">
              SHADOW MODE
            </span>
            <span className="text-sm font-normal text-gray-500">
              - Assume ton ombre 🖤
            </span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[70vh] pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              >
                <Moon className="w-8 h-8 text-purple-500" />
              </motion.div>
            </div>
          ) : needsQuiz && !showQuiz ? (
            // Intro avec GRAND VISAGE de Nyx ou Kurson
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative min-h-[500px] flex flex-col"
            >
              {/* 🖤 GRAND VISAGE - 50% de l'écran */}
              <div className="relative h-[280px] overflow-hidden rounded-xl mb-4">
                {/* Background gradient sombre */}
                <div className="absolute inset-0 bg-gradient-to-b from-purple-900/50 via-black to-black" />
                
                {/* Particules flottantes */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  {[...Array(12)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-1 h-1 rounded-full bg-purple-500/40"
                      style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                      }}
                      animate={{
                        y: [0, -20, 0],
                        opacity: [0.2, 0.7, 0.2],
                      }}
                      transition={{
                        duration: 3 + Math.random() * 2,
                        repeat: Infinity,
                        delay: Math.random() * 2,
                      }}
                    />
                  ))}
                </div>
                
                {/* Le GRAND visage de Nyx (ou Kurson selon le genre) */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', bounce: 0.3, duration: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  {/* Glow effect */}
                  <div 
                    className="absolute w-48 h-48 rounded-full blur-3xl opacity-40"
                    style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)' }}
                  />
                  
                  {/* L'image de Nyx - SANS CADRE */}
                  <motion.img
                    src={GUIDE_IMAGES.nyx?.confident_smirk || GUIDE_IMAGES.nyx?.default}
                    alt="Nyx - Guide de l'Ombre"
                    className="relative w-56 h-56 object-cover rounded-full"
                    style={{
                      boxShadow: '0 0 60px rgba(139, 92, 246, 0.4), 0 20px 40px rgba(0,0,0,0.6)'
                    }}
                    animate={{ 
                      y: [0, -8, 0],
                    }}
                    transition={{ 
                      duration: 4, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }}
                  />
                </motion.div>
                
                {/* Nom et titre en bas */}
                <div className="absolute bottom-4 left-0 right-0 text-center">
                  <motion.h3 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-3xl font-black text-white drop-shadow-lg"
                  >
                    Nyx
                  </motion.h3>
                  <motion.p 
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="text-purple-300 text-sm italic"
                  >
                    Gardienne de l'Ombre
                  </motion.p>
                </div>
              </div>
              
              {/* Message de bienvenue */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-center px-4"
              >
                <p className="text-gray-300 mb-4 text-lg italic">
                  &ldquo;Bienvenue dans l&apos;ombre... Ici, on ne joue plus les gentils.&rdquo;
                </p>
                
                <p className="text-gray-500 mb-6 text-sm">
                  Les 7 péchés capitaux révèlent ta vraie nature. 
                  Prêt(e) à découvrir quel péché te domine ?
                </p>
              </motion.div>
              
              {/* Badges des péchés */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap justify-center gap-2 mb-6 px-4"
              >
                {Object.values(SEVEN_SINS).map((sin, i) => (
                  <motion.span
                    key={sin.id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.8 + i * 0.05 }}
                    whileHover={{ scale: 1.1 }}
                    className={`px-3 py-1 rounded-full text-xs bg-gradient-to-r ${sin.darkColor} border border-gray-700`}
                  >
                    {sin.emoji} {sin.name}
                  </motion.span>
                ))}
              </motion.div>
              
              {/* Bouton CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="px-4"
              >
                <Button
                  onClick={() => setShowQuiz(true)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 text-lg font-bold rounded-xl"
                  data-testid="shadow-reveal-button"
                >
                  <Flame className="w-5 h-5 mr-2" />
                  Révéler mon ombre
                </Button>
              </motion.div>
            </motion.div>
          ) : showQuiz ? (
            // Quiz des péchés
            <ShadowQuiz
              onComplete={handleQuizComplete}
              onCancel={() => setShowQuiz(false)}
            />
          ) : activeRoom ? (
            // Vue d'un salon
            <div className="space-y-4">
              {/* Room header */}
              <div className={`p-4 rounded-xl bg-gradient-to-r ${activeRoom.color}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{activeRoom.emoji}</span>
                    <div>
                      <h3 className="font-bold text-white">{activeRoom.name}</h3>
                      <p className="text-xs text-white/60">{activeRoom.description}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setActiveRoom(null)}
                    className="text-white hover:bg-white/20"
                  >
                    ← Retour
                  </Button>
                </div>
              </div>
              
              {/* Welcome message */}
              <div className="p-4 rounded-xl bg-purple-900/20 border border-purple-500/30 text-center">
                <p className="text-purple-300">{activeRoom.welcomeMessage}</p>
                <p className="text-xs text-gray-500 mt-2 italic">
                  Règles : {activeRoom.rules}
                </p>
              </div>
              
              {/* Placeholder chat */}
              <div className="h-48 rounded-xl bg-gray-900 flex items-center justify-center">
                <p className="text-gray-500">
                  💬 Chat en temps réel bientôt disponible...
                </p>
              </div>
            </div>
          ) : (
            // Liste des salons + profil shadow
            <div className="space-y-6">
              {/* Mon péché */}
              {shadowProfile?.dominantSin && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-gradient-to-br from-purple-900/50 to-black border border-purple-500/30"
                >
                  <div className="flex items-center gap-4">
                    <SinBadge sin={shadowProfile.dominantSin} size="lg" />
                    <div className="flex-1">
                      <h4 className="font-bold text-white">
                        Ton péché dominant
                      </h4>
                      <p className="text-sm text-gray-400">
                        {SEVEN_SINS[shadowProfile.dominantSin]?.description}
                      </p>
                      {shadowProfile.secondarySin && (
                        <p className="text-xs text-gray-500 mt-1">
                          Secondaire : {SEVEN_SINS[shadowProfile.secondarySin]?.emoji} {SEVEN_SINS[shadowProfile.secondarySin]?.name}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowQuiz(true)}
                      className="border-purple-500/50 text-purple-300"
                    >
                      Refaire
                    </Button>
                  </div>
                </motion.div>
              )}
              
              {/* 🌙 NYX 2.0 - Invitation à aller plus profond */}
              {shadowProfile?.quizCompleted && !nyx2Profile && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="p-4 rounded-xl bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border border-indigo-500/30"
                >
                  <div className="flex items-center gap-4">
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                      className="text-4xl"
                    >
                      🌙
                    </motion.div>
                    <div className="flex-1">
                      <h4 className="font-bold text-white flex items-center gap-2">
                        Nyx t&apos;invite plus profond...
                        <span className="text-xs px-2 py-0.5 bg-indigo-500/50 rounded-full">NOUVEAU</span>
                      </h4>
                      <p className="text-sm text-indigo-300 mt-1">
                        Tu as révélé tes péchés... Maintenant, descends dans les méandres de ton subconscient.
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowNyx2Quiz(true)}
                      className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
                      size="sm"
                    >
                      <Moon className="w-4 h-4 mr-1" />
                      Explorer
                    </Button>
                  </div>
                </motion.div>
              )}
              
              {/* 🌙 NYX 2.0 - Profil complété */}
              {nyx2Profile && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl bg-gradient-to-br from-indigo-900/30 to-purple-900/30 border border-indigo-500/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-white flex items-center gap-2">
                      <Moon className="w-4 h-4 text-indigo-400" />
                      Ton Profil d&apos;Ombre Profond
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowNyx2Quiz(true)}
                      className="text-indigo-300 text-xs"
                    >
                      Refaire
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 rounded-lg bg-black/30">
                      <span className="text-gray-500 text-xs">Attachement</span>
                      <p className="text-white">
                        {ATTACHMENT_STYLES[nyx2Profile.attachmentStyle]?.emoji} {ATTACHMENT_STYLES[nyx2Profile.attachmentStyle]?.name}
                      </p>
                    </div>
                    <div className="p-2 rounded-lg bg-black/30">
                      <span className="text-gray-500 text-xs">Blessure</span>
                      <p className="text-white">
                        {FIVE_WOUNDS[nyx2Profile.dominantWound]?.emoji} {FIVE_WOUNDS[nyx2Profile.dominantWound]?.name}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
              
              {/* Salons de l'ombre */}
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <Flame className="w-5 h-5 text-red-500" />
                  Les Salons de l&apos;Ombre
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Chaque péché a son sanctuaire... Trouve ta tribu sombre.
                </p>
                
                <div className="grid gap-3">
                  {SHADOW_ROOMS.map(room => (
                    <ShadowRoomCard
                      key={room.id}
                      room={room}
                      onlineCount={onlineCounts[room.id] || 0}
                      onClick={() => handleJoinRoom(room)}
                      isJoined={joinedRooms.includes(room.id)}
                      userSin={shadowProfile?.dominantSin}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Guide Shadow */}
        <AnimatePresence>
          {showGuide && (
            <ShadowGuide
              guide={guideCharacter}
              messageType={guideType}
              sin={shadowProfile?.dominantSin}
              onDismiss={() => setShowGuide(false)}
              position="bottom-right"
            />
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
    
    {/* 🌙 NYX 2.0 - Quiz Modal */}
    <Dialog open={showNyx2Quiz} onOpenChange={setShowNyx2Quiz}>
      <DialogContent className="sm:max-w-lg bg-gray-900 border-indigo-500/30 max-h-[90vh] overflow-y-auto">
        <Nyx2Quiz
          onComplete={handleNyx2Complete}
          onClose={() => setShowNyx2Quiz(false)}
          existingProfile={nyx2Profile}
          isDarkMode={true}
          displayName={customer?.firstName || profile?.displayName || ''}
        />
      </DialogContent>
    </Dialog>
  </>
  );
};

export default ShadowMode;
